'use client'

import { RESOURCE_URL } from '@telesoft/lib/web/constants'
import { Admin, Form, Page, Post, Resource } from '@telesoft/types/payload-types'
import Divider from '@telesoft/ui/blocks/Divider'
import StandaloneHeaderCluster from '@telesoft/ui/blocks/StandaloneHeaderCluster'
import Subscription, { defaultBlockJSON } from '@telesoft/ui/blocks/SubscribeToNucleusCTA'
import ArticleHero from '@telesoft/ui/components/articles/article-hero'
import ResourceGridSection from '@telesoft/ui/components/sections/resource-grid-section'
import Container from '@telesoft/ui/components/ui/container'
import RichTextContainer from '@telesoft/ui/components/ui/rich-text-container'
import ArticlesAuthorInfo from '@telesoft/ui/containers/articles-author-info'
import ModalForm from '@telesoft/ui/v2/components/ModalForm'
import { RenderBlocks } from '@telesoft/ui/v2/components/RenderBlocks'
import { RichText } from '@telesoft/ui/v2/components/RichText'
import { Button } from '@telesoft/ui/v2/components/ui/button'
import React, { Fragment } from 'react'

interface Props {
  post: Post | PostWithResource
  recommendedPosts?: Post[]
}

export type PostWithResource = Post & {
  resource: {
    download: Resource
  }
}

export default function ArticlePage({ post, recommendedPosts }: Props) {
  const isPostWithResource =
    'resource' in post && 'download' in post.resource! && post.resource.download !== typeof 'string'

  const author = post?.author as Admin

  const recommendedPostsTitle = {
    subTitle: 'NUCLEUS',
    title: 'Further Reading',
    options: { hasButtons: false },
  }

  const resource = post?.resource?.download as Resource | undefined
  const isResourceWalled = resource?.isWalled
  const downloadUrl = RESOURCE_URL + '/' + resource?.filename

  return (
    <React.Fragment>
      <Container>
        <ArticleHero hasDownload={isPostWithResource} article={post} />
        <div id="article" className="relative">
          <ArticlesAuthorInfo author={author} article={post} />
          <Divider />
          <RichTextContainer>
            <RichText className="max-w-full mx-auto" content={post?.content} />
          </RichTextContainer>
          {isPostWithResource && (
            <Fragment>
              <Divider />
              {isResourceWalled ? (
                <ModalForm cmsForm={resource.form as Form} />
              ) : (
                <Button asChild>
                  <a href={downloadUrl} target="_blank">
                    Download Now
                  </a>
                </Button>
              )}
            </Fragment>
          )}
          <RenderBlocks blocks={post.blocks as Page['blocks']} />
        </div>
      </Container>
      {recommendedPosts && recommendedPosts.length > 0 && (
        <Container>
          <Divider />
          <StandaloneHeaderCluster {...recommendedPostsTitle} />
          <ResourceGridSection data={recommendedPosts} />
        </Container>
      )}
      <Divider />
      <Subscription {...defaultBlockJSON} />
    </React.Fragment>
  )
}
